import { useNavigate } from 'react-router-dom';
import { ScreenShell } from '../shell/ScreenShell';
import { PaymentSummaryCard } from '../domain/PaymentSummaryCard';
import { Button } from '../design-system/Button';
import { Text } from '../design-system/Text';
import { OfflineState } from '../feedback/OfflineState';
import { useSessionStore } from '../../stores/sessionStore';
import { useUiStore } from '../../stores/uiStore';
import { usePrototypeStore } from '../../stores/prototypeStore';
import { analytics } from '../../lib/analytics';
import { challanFixtures } from '../../fixtures/challans';
import { formatCurrency } from '../../utils/format';
import type { ListLength } from '../../types';
import { ShieldCheck, CreditCard } from 'lucide-react';

export function PaymentSummaryScreen() {
  const navigate = useNavigate();
  const selectedIds = useSessionStore((s) => s.selectedChallanIds);
  const completePayment = useSessionStore((s) => s.completePayment);
  const isOffline = useUiStore((s) => s.isOffline);
  const simulateOffline = usePrototypeStore((s) => s.simulateOffline);
  const listLength: ListLength = usePrototypeStore((s) => s.listLength);
  const offline = isOffline || simulateOffline;

  const allChallans = challanFixtures[listLength];
  const selectedChallans = allChallans.filter((c) => selectedIds.includes(c.id));
  const subtotal = selectedChallans.reduce((sum, c) => sum + c.amount, 0);
  const fee = Math.round(subtotal * 0.02);
  const total = subtotal + fee;

  const handleProceed = () => {
    analytics.emit('proceed_to_pay_click', {
      total,
      count: selectedChallans.length,
    });
    completePayment();
    navigate('/payment-success');
  };

  return (
    <ScreenShell title="Payment Summary" showBack>
      <div className="flex flex-col gap-4 p-4">
        {offline && <OfflineState />}

        <PaymentSummaryCard challans={selectedChallans} />

        <div className="rounded-xl border border-slate-100 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-park-50 text-park-600 dark:bg-park-900/30 dark:text-park-400">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <Text variant="body-medium">UPI / Netbanking</Text>
                <Text variant="caption" color="secondary">
                  Pay securely via Park+
                </Text>
              </div>
            </div>
            <ShieldCheck className="h-5 w-5 text-emerald-500" />
          </div>
        </div>
      </div>

      <div className="mt-auto border-t border-slate-100 bg-white p-4 pb-6 dark:border-slate-800 dark:bg-slate-800">
        <div className="mb-3 flex items-center justify-between">
          <Text variant="body-medium">Total payable</Text>
          <Text variant="display-medium" color="primary-brand">
            {formatCurrency(total)}
          </Text>
        </div>
        <Button
          size="lg"
          fullWidth
          onClick={handleProceed}
          disabled={selectedChallans.length === 0 || offline}
          aria-label="Proceed to Pay"
        >
          Proceed to Pay {formatCurrency(total)}
        </Button>
      </div>
    </ScreenShell>
  );
}
